'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { ExtendedNeighborhood } from './NeighborhoodItem';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsMobile } from '@/hooks/useDeviceType';
import { cn } from '@/lib/cn';
import { NeighborhoodHoverTooltip } from './NeighborhoodHoverTooltip';

// Type declarations for Google Maps API
declare global {
  interface Window {
    google: typeof google;
  }
}

interface PolygonData {
  id: string;
  name: string;
  paths: { lat: number; lng: number }[];
  children?: PolygonData[];
}

export interface NeighborhoodMapCoreProps {
  selectedNeighborhoodIds: string[];
  onNeighborhoodClick: (neighborhoodId: string) => void;
  neighborhoods: ExtendedNeighborhood[];
  height?: string | number;
  width?: string | number;
  minHeight?: string | number;
  minWidth?: string | number;
}

const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '';

const DEFAULT_CENTER = { lat: 40.754159399499386, lng: -73.95362145902037 };
const DEFAULT_ZOOM = 12;

type NeighborhoodHoverEvent = {
  id: string;
  name: string;
  clientX: number;
  clientY: number;
} | null;

function isValidCoordinate(coord: number): boolean {
  return typeof coord === 'number' && !isNaN(coord) && isFinite(coord);
}

function transformPolygonData(neighborhoods: ExtendedNeighborhood[]): PolygonData[] {
  const transformed: PolygonData[] = [];

  // Helper to recursively add children
  const addChildren = (parent: PolygonData) => {
    const children = neighborhoods.filter(n => n.parentId === parent.id);

    children.forEach(child => {
      const childData: PolygonData = {
        id: child.id,
        name: child.name,
        paths: [],
      };

      // Access mapData from the neighborhood object
      const mapData = child.mapData;
      if (mapData?.polygon && Array.isArray(mapData.polygon)) {
        const validPaths = mapData.polygon
          .filter(
            (coord: [number, number]) =>
              Array.isArray(coord) &&
              coord.length === 2 &&
              isValidCoordinate(coord[0]) &&
              isValidCoordinate(coord[1]),
          )
          .map(([lng, lat]: [number, number]) => ({ lat, lng }));

        if (validPaths.length > 0) {
          childData.paths = validPaths;
        } else {
          return;
        }
      } else {
        return;
      }

      if (!parent.children) {
        parent.children = [];
      }

      parent.children.push(childData);
      addChildren(childData);
    });
  };

  // First pass: Create top-level parents
  neighborhoods.forEach(neighborhood => {
    if (neighborhood.parentId) return;

    const parentData: PolygonData = {
      id: neighborhood.id,
      name: neighborhood.name,
      paths: [],
    };

    const mapData = neighborhood.mapData;
    if (mapData?.polygon && Array.isArray(mapData.polygon)) {
      const validPaths = mapData.polygon
        .filter(
          (coord: [number, number]) =>
            Array.isArray(coord) &&
            coord.length === 2 &&
            isValidCoordinate(coord[0]) &&
            isValidCoordinate(coord[1]),
        )
        .map(([lng, lat]: [number, number]) => ({ lat, lng }));

      if (validPaths.length > 0) {
        parentData.paths = validPaths;
      } else {
        return;
      }
    } else {
      return;
    }

    transformed.push(parentData);
    addChildren(parentData);
  });

  return transformed;
}

function MapPolygons({
  polygons,
  selectedIds,
  onPolygonClick,
  neighborhoods,
  onPolygonHover,
}: {
  polygons: PolygonData[];
  selectedIds: Set<string>;
  onPolygonClick: (id: string) => void;
  neighborhoods: ExtendedNeighborhood[];
  onPolygonHover: (evt: NeighborhoodHoverEvent) => void;
}) {
  const map = useMap();

  const [hoveredPolygonId, setHoveredPolygonId] = useState<string | null>(null);
  const polygonRefsRef = React.useRef<google.maps.Polygon[]>([]);
  const isMobile = useIsMobile();
  const canHover = !isMobile;

  useEffect(() => {
    if (!map || typeof window === 'undefined' || !window.google) return;

    // Clear existing polygons
    polygonRefsRef.current.forEach((polygon: google.maps.Polygon) => {
      if (polygon) {
        polygon.setMap(null);
      }
    });
    polygonRefsRef.current = [];

    const createPolygon = (polygonData: PolygonData, borough: string) => {
      if (polygonData.paths.length === 0) return;

      const isSelected = selectedIds.has(polygonData.id);
      const isHovered = canHover && hoveredPolygonId === polygonData.id;

      // Set colors based on state
      // Selected: rgba(92, 92, 255, 0.75) -> #5C5CFF with opacity 0.75
      // Hover: rgba(117, 127, 255, 0.50) -> #757FFF with opacity 0.50
      // Default stroke: #64748B
      let fillColor = 'transparent';
      let fillOpacity = 0;

      if (isSelected) {
        fillColor = '#5C5CFF'; // rgb(92, 92, 255)
        fillOpacity = 0.75;
      } else if (isHovered) {
        fillColor = '#757FFF'; // rgb(117, 127, 255)
        fillOpacity = 0.5;
      }

      const polygon = new window.google.maps.Polygon({
        paths: polygonData.paths,
        fillColor,
        fillOpacity,
        strokeColor: '#64748B',
        strokeOpacity: 1,
        strokeWeight: 2,
        clickable: true,
      });

      polygon.addListener('click', () => {
        // On mobile, hover state can "stick" after a tap. Clear hover on click.
        setHoveredPolygonId(null);
        onPolygonClick(polygonData.id);
      });

      if (canHover) {
        polygon.addListener('mouseover', (e: google.maps.MapMouseEvent) => {
          setHoveredPolygonId(polygonData.id);
          const domEvent = e.domEvent as MouseEvent | undefined;
          if (domEvent) {
            onPolygonHover({
              id: polygonData.id,
              name: polygonData.name,
              clientX: domEvent.clientX,
              clientY: domEvent.clientY,
            });
          }
        });

        polygon.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
          const domEvent = e.domEvent as MouseEvent | undefined;
          if (!domEvent) return;
          onPolygonHover({
            id: polygonData.id,
            name: polygonData.name,
            clientX: domEvent.clientX,
            clientY: domEvent.clientY,
          });
        });

        polygon.addListener('mouseout', () => {
          setHoveredPolygonId(null);
          onPolygonHover(null);
        });
      }

      polygon.setMap(map);
      polygonRefsRef.current.push(polygon);

      // Render children recursively
      if (polygonData.children) {
        polygonData.children.forEach(child => {
          const childNeighborhood = neighborhoods.find(n => n.id === child.id);
          createPolygon(child, childNeighborhood?.borough || borough);
        });
      }
    };

    polygons.forEach(polygon => {
      const neighborhood = neighborhoods.find(n => n.id === polygon.id);
      createPolygon(polygon, neighborhood?.borough || '');
    });

    return () => {
      polygonRefsRef.current.forEach((polygon: google.maps.Polygon) => {
        if (polygon) {
          polygon.setMap(null);
        }
      });
      polygonRefsRef.current = [];
    };
  }, [
    map,
    polygons,
    selectedIds,
    hoveredPolygonId,
    onPolygonClick,
    neighborhoods,
    canHover,
    onPolygonHover,
  ]);

  return null;
}

function NeighborhoodMapContent({
  selectedNeighborhoodIds,
  onNeighborhoodClick,
  neighborhoods,
  height = '100%',
  width = '100%',
}: NeighborhoodMapCoreProps) {
  const analytics = useAnalytics();
  const [hoveredNeighborhood, setHoveredNeighborhood] = useState<NeighborhoodHoverEvent>(null);

  const transformedPolygons = useMemo(() => {
    return transformPolygonData(neighborhoods);
  }, [neighborhoods]);

  const selectedSet = useMemo(() => {
    return new Set(selectedNeighborhoodIds);
  }, [selectedNeighborhoodIds]);

  const handlePolygonClick = useCallback(
    (id: string) => {
      analytics.track('listings_filter_allneighborhoods_map_neighborhood');
      analytics.track(
        selectedSet.has(id)
          ? 'listings_filter_allneighborhoods_map_unselect'
          : 'listings_filter_allneighborhoods_map_select',
      );
      onNeighborhoodClick(id);
    },
    [analytics, onNeighborhoodClick, selectedSet],
  );

  const mapHeight = typeof height === 'number' ? `${height}px` : height;
  const mapWidth = typeof width === 'number' ? `${width}px` : width;

  const handlePolygonHover = useCallback((evt: NeighborhoodHoverEvent) => {
    setHoveredNeighborhood(evt);
  }, []);

  return (
    <div
      style={{
        width: mapWidth,
        height: mapHeight,
        position: 'relative',
      }}
    >
      <NeighborhoodHoverTooltip
        open={Boolean(hoveredNeighborhood)}
        neighborhoodName={hoveredNeighborhood?.name ?? ''}
        anchor={
          hoveredNeighborhood
            ? { clientX: hoveredNeighborhood.clientX, clientY: hoveredNeighborhood.clientY }
            : null
        }
      />
      <Map
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        gestureHandling="greedy"
        disableDefaultUI={false}
        zoomControl={true}
        style={{ width: mapWidth, height: mapHeight }}
        clickableIcons={false}
        fullscreenControl={false}
        mapTypeControl={false}
        streetViewControl={false}
      >
        <MapPolygons
          polygons={transformedPolygons}
          selectedIds={selectedSet}
          onPolygonClick={handlePolygonClick}
          neighborhoods={neighborhoods}
          onPolygonHover={handlePolygonHover}
        />
      </Map>
    </div>
  );
}

export function NeighborhoodMapCore({
  height = '340px',
  width = '100%',
  minHeight,
  minWidth,
  ...props
}: NeighborhoodMapCoreProps) {
  if (!GOOGLE_MAPS_API_KEY) {
    const containerStyle: React.CSSProperties = {
      width: typeof width === 'number' ? `${width}px` : width,
      height: typeof height === 'number' ? `${height}px` : height,
    };
    if (minHeight) {
      containerStyle.minHeight = typeof minHeight === 'number' ? `${minHeight}px` : minHeight;
    }
    if (minWidth) {
      containerStyle.minWidth = typeof minWidth === 'number' ? `${minWidth}px` : minWidth;
    }

    return (
      <div
        className="flex items-center justify-center bg-secondary rounded-2xl"
        style={containerStyle}
      >
        <p className="text-body-2 text-secondary">Google Maps API key not configured</p>
      </div>
    );
  }

  const containerStyle: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    position: 'relative',
  };
  if (minHeight) {
    containerStyle.minHeight = typeof minHeight === 'number' ? `${minHeight}px` : minHeight;
  }
  if (minWidth) {
    containerStyle.minWidth = typeof minWidth === 'number' ? `${minWidth}px` : minWidth;
  }

  return (
    <div style={containerStyle}>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <NeighborhoodMapContent height={height} width={width} {...props} />
      </APIProvider>
    </div>
  );
}
